import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {
  FiCrosshair,
  FiEyeOff,
  FiDollarSign,
  FiArrowRight,
  FiAlertTriangle,
  FiBookOpen,
} = FiIcons;

const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0 } };

const Truth = () => {
  return (
    <div className="pt-24 pb-20">
      <Helmet>
        <title>The Truth | Video Examples</title>
        <meta name="description" content="Why we built Video Examples. A manifesto for quality in a world of noise." />
      </Helmet>

      <motion.div
        variants={fadeUp}
        initial="hidden"
        animate="visible"
        transition={{ duration: 0.6 }}
        className="container mx-auto px-6 max-w-6xl"
      >
        {/* Logo */}
        <div className="text-center mb-12">
          <Link to="/" className="inline-block">
            <h2 className="text-4xl font-black text-white tracking-tight leading-none">
              Video<span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-amber-500 to-orange-600">Examples</span>
            </h2>
          </Link>
        </div>

        {/* Hero Section */}
        <div className="text-center mb-20">
          <h1 className="text-5xl md:text-6xl font-black leading-tight tracking-tight">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-white to-white/60">
              Stop playing SEO roulette with your video budget.
            </span>
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto mt-6 text-lg leading-relaxed">
            Finding the perfect production partner shouldn't be the hardest part of your project.
            We're here to cut through the industry noise.
          </p>
        </div>

        {/* Problem Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-20 max-w-6xl mx-auto text-left">

          {/* Card 1 — The SEO Trap */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0 }}
            className="glass-card p-8 flex flex-col"
          >
            <SafeIcon icon={FiEyeOff} className="text-orange-500 text-3xl mb-4" />
            <h3 className="text-lg font-bold text-white mb-3">The SEO Trap</h3>
            <p className="text-sm text-slate-400 leading-relaxed">
              If you search for an agency right now, you won't necessarily find the best ones. You'll find the ones with the best SEO. Massive agencies dominate search results, burying incredibly talented boutique studios.
            </p>
          </motion.div>

          {/* Card 2 — Shopping in a Mall */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="glass-card p-8 flex flex-col"
          >
            <SafeIcon icon={FiCrosshair} className="text-blue-500 text-3xl mb-4" />
            <h3 className="text-lg font-bold text-white mb-3">Shopping in a Mall</h3>
            <p className="text-sm text-slate-400 leading-relaxed">
              Hiring blindly is like walking into a mall. You forget what you came for, get distracted by flashy showreels, and end up buying into a team that doesn't understand your B2B goals.
            </p>
            <div className="mt-auto pt-6">
              <a
                href="https://www.whatastory.agency/blog/how-to-hire-the-perfect-video-agency-for-your-business"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
              >
                Read: How to hire the perfect video agency →
              </a>
            </div>
          </motion.div>

          {/* Card 3 — The $18,000 Nightmare */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="glass-card p-8 flex flex-col"
          >
            <SafeIcon icon={FiDollarSign} className="text-red-500 text-3xl mb-4" />
            <h3 className="text-lg font-bold text-white mb-3">The $18,000 Nightmare</h3>
            <p className="text-sm text-slate-400 leading-relaxed">
              We've seen founders burn tens of thousands on the 'wrong' agency because it 'felt right'. They wait months, only to receive a recycled template with public domain music.
            </p>
            <div className="mt-auto pt-6">
              <a
                href="https://www.whatastory.agency/resources/ai-quote"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-red-400 hover:text-red-300 transition-colors"
              >
                Get an Instant Quote instead →
              </a>
            </div>
          </motion.div>

        </div>

        {/* Solution Story */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mt-32 max-w-4xl mx-auto"
        >
          <h2 className="text-3xl font-bold text-white mb-6">Why we built VideoExamples.</h2>
          <div className="border-l-4 border-orange-500 pl-6 space-y-4 text-gray-300 text-lg">
            <p>
              Full disclosure: We are a video production company ourselves (What a Story). But we are creative folks, not traders. We frequently turn down projects if we know we aren't the absolute perfect fit for a client's industry.
            </p>
            <p>
              The reality is, there is no 'one-size-fits-all'. Every studio has a unique superpower. Even we hate seeing the market get this polluted. We built this space to bypass the search algorithms and connect the right dots with the right goals.
            </p>
          </div>
          <div className="mt-8">
            <a
              href="https://www.whatastory.agency/blog/how-to-shortlist-a-video-agency-for-explainer-videos"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-orange-400 hover:text-orange-300 transition-colors"
            >
              <SafeIcon icon={FiBookOpen} className="inline mr-2" />
              Read: How to shortlist a video agency for explainer videos →
            </a>
          </div>
        </motion.div>

        {/* Enterprise Highlight */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="glass-card bg-blue-900/10 border-blue-500/30 p-8 mt-16 text-center"
        >
          <SafeIcon icon={FiAlertTriangle} className="text-blue-400 text-4xl mx-auto mb-4" />
          <p className="text-gray-300 text-lg leading-relaxed max-w-3xl mx-auto">
            If you are looking for an enterprise solution, you need a production company with rock-solid processes yet modern bleeding-edge technology and creativity. There are only a few agencies that fit this cut. Be careful out there. Need help deciding?{' '}
            <a
              href="mailto:hello@whatastory.agency"
              className="text-blue-400 hover:text-blue-300 transition-colors underline underline-offset-2"
            >
              Let me know.
            </a>
          </p>
        </motion.div>

        {/* Final CTA */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mt-24 text-center relative"
        >
          <h2 className="text-3xl md:text-4xl font-black text-white">Shortlist with Confidence.</h2>
          <p className="text-gray-400 text-lg mt-4 max-w-2xl mx-auto leading-relaxed">
            The market is too crowded to just guess. We didn't just build a gallery; we built a comparison engine.
          </p>
          <Link
            to="/directory"
            className="btn-primary inline-flex items-center gap-2 mt-8 px-8 py-4"
          >
            Compare Video Companies
            <SafeIcon icon={FiArrowRight} />
          </Link>
        </motion.div>

      </motion.div>
    </div>
  );
};

export default Truth;